/**
 * AddSessionCredentialDialog — manual credential add from keybook into session.
 *
 * User picks a keybook entry; entries already bound in the session are disabled.
 * Selected entry is decrypted client-side, env var names are auto-generated from
 * the label + field names, and the caller receives the full binding info.
 */
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Search, Key, BookKey, Loader2 } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { sanitizeEnvName } from '@papan/sandbox/env-names'
import { errorText } from '@papan/api-client'

interface KeybookEntryMeta {
  id: string
  label: string
  credentialType?: string
  fileId?: string | null
}

interface ResolvedAdd {
  entryId: string
  entryLabel: string
  envMapping: Record<string, string>
  values: Record<string, string>
  file?: string
}

interface Props {
  /** Entry IDs already bound to this session — shown disabled */
  existingEntryIds: Set<string>
  onAdd: (resolved: ResolvedAdd) => void
  onClose: () => void
}

/** label="GitHub Token" + field="token" → GITHUB_TOKEN (dedup if field in label). A suggestion: the session's
 *  addFromKeybook is the one place that makes it usable (finalizes it, renames a collision). Exported for its test. */
export function genEnvName(label: string, fieldName: string): string {
  const lp = sanitizeEnvName(label) || 'CRED'
  const fp = sanitizeEnvName(fieldName)
  // If the label already contains the field word, don't repeat it.
  if (!fp || lp.split('_').includes(fp)) return lp
  return `${lp}_${fp}`
}

export function AddSessionCredentialDialog({ existingEntryIds, onAdd, onClose }: Props) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [entries, setEntries] = useState<KeybookEntryMeta[]>([])
  const [filter, setFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [resolvingId, setResolvingId] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { keybookApi, getKeybookContext } = await import('@papan/keybook/client')
        const { tenantId } = getKeybookContext()
        if (!tenantId) throw new Error('No active tenant')
        const { entries: list } = await keybookApi.listEntries(tenantId)
        if (cancelled) return
        setEntries(list.map(e => ({
          id: e.id,
          label: e.sourceLabel,
          credentialType: e.credentialType,
          fileId: e.fileIds?.[0] ?? null,
        })))
      } catch {
        if (!cancelled) setError('Failed to load credentials')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const filtered = filter
    ? entries.filter(e => e.label.toLowerCase().includes(filter.toLowerCase()))
    : entries

  async function handleSelect(entry: KeybookEntryMeta) {
    if (existingEntryIds.has(entry.id)) return
    setResolvingId(entry.id)
    setError(null)
    try {
      const { keybookApi } = await import('@papan/keybook/client')
      const fullEntry = await keybookApi.getEntry(entry.id)
      const data = (fullEntry.entry.data ?? { fields: {} }) as { fields: Record<string, string> }

      // Try to resolve file path from fileId
      let file: string | undefined
      if (entry.fileId) {
        try {
          const { filesApi } = await import('@papan/files/client')
          const f = await filesApi.getFileInfo(entry.fileId)
          if (f?.path) file = f.path
        } catch { /* ignore, file lookup is optional */ }
      }

      // Generate env var names for every field that has a value
      const envMapping: Record<string, string> = {}
      for (const [fieldName] of Object.entries(data.fields)) {
        envMapping[fieldName] = genEnvName(entry.label, fieldName)
      }

      onAdd({
        entryId: entry.id,
        entryLabel: entry.label,
        envMapping,
        values: data.fields,
        file,
      })
      onClose()
    } catch (err) {
      const text = errorText(err, 'Failed to decrypt credential')   // #1316: a dropped answer shows nothing
      if (text !== null) setError(text)
      setResolvingId(null)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`${themeClass} relative z-10 w-full max-w-sm rounded-xl shadow-xl themed-bg-secondary`}>
        <div className="flex items-center gap-2 px-4 py-3 border-b themed-border">
          <BookKey size={16} className="themed-icon" />
          <span className="text-sm font-medium themed-text flex-1">Add Credential to Session</span>
          <button onClick={onClose} className="themed-text-muted hover:themed-text">
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-2 border-b themed-border">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 themed-text-muted" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="themed-input w-full pl-8 pr-3 py-1.5 rounded-lg text-sm"
              placeholder="Search credentials..."
              autoFocus
            />
          </div>
        </div>

        {error && (
          <div className="px-4 py-2 text-xs text-red-400 border-b themed-border">{error}</div>
        )}

        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-xs themed-text-muted flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading credentials...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-xs themed-text-muted">
              {entries.length === 0 ? 'No credentials saved in keybook yet.' : 'No matches.'}
            </div>
          ) : (
            filtered.map(entry => {
              const alreadyBound = existingEntryIds.has(entry.id)
              const isResolving = resolvingId === entry.id
              return (
                <button
                  key={entry.id}
                  onClick={() => handleSelect(entry)}
                  disabled={alreadyBound || isResolving}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors border-b themed-border ${
                    alreadyBound ? 'opacity-40 cursor-not-allowed' : 'hover:themed-bg-tertiary'
                  }`}
                >
                  <Key size={14} className={alreadyBound ? 'text-zinc-600' : 'text-yellow-400'} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm themed-text truncate">{entry.label}</div>
                    <div className="text-[10px] themed-text-muted">
                      {alreadyBound ? 'already in session' : entry.credentialType || 'credential'}
                    </div>
                  </div>
                  {isResolving && <Loader2 size={14} className="animate-spin themed-text-muted" />}
                </button>
              )
            })
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
